import type { AppLocale } from '@floorfile/i18n'

/**
 * How this package's non-English catalogues reach the runtime registry.
 *
 * `apps/web/src/app/locale-catalogues.ts` discovers every catalogue with
 * `import.meta.glob('../**\/copy.*.ts')` and derives each namespace from the
 * directory it sits in. That glob is rooted in `apps/web/src` and cannot see
 * another package, so this one is registered **explicitly** — the app imports
 * this map and merges it into the same load-and-register pass.
 *
 * Two properties are worth keeping:
 *
 * - **The dynamic `import()` is preserved** (D-L6, and the
 *   `no-static-locale-imports` rule). `manualChunks` in `apps/web/vite.config.ts`
 *   matches `**\/copy.<locale>.ts` by path with no package restriction, so
 *   `copy.fr.ts` here joins the same single `locale-fr` chunk as the 12 feature
 *   catalogues rather than being pulled into the design-system chunk that every
 *   screen imports.
 * - **The namespace is written once**, in `UI_NAMESPACE`, and asserted against
 *   `defineCopy('ui', …)` by `apps/web/src/app/__tests__/copy-catalogues.test.ts`.
 *   Everywhere else the namespace is derived from the path twice and the test
 *   checks the two agree; here there is only one source, so the test checks the
 *   catalogue against it instead.
 *
 * Adding a locale is one line here plus a `copy.<code>.ts` beside it. The test
 * compares this map's keys against the files actually on disk, so a forgotten
 * line fails rather than silently rendering English.
 */
export const UI_NAMESPACE = 'ui'

type CatalogueLoader = () => Promise<unknown>

export const uiTranslationLoaders: Readonly<Partial<Record<AppLocale, CatalogueLoader>>> = {
  fr: () => import('./copy.fr'),
}
